/**
 * anlage-ballsets — die Vorschau-Bloecke 1, 2, 4 und 5.
 *
 * Block 3 (die Set-Varianten-Matrix) steht in `SetMatrixTabelle.tsx`, weil er als
 * einziger eine eigene Tabellen-Logik hat und sonst diese Datei dominieren wuerde.
 *
 * Panel-Muster ist bewusst das schlichte `div` mit `border-[#DAD9D9]` und nicht die
 * shadcn-`Card`: die wird im ganzen Monorepo nirgends benutzt (Nutzer-Anpassung vom
 * 15.09.2026), und ein Programm, das als einziges anders aussieht, faellt negativ auf.
 *
 * Artikeltexte aus Plenty sind Fremdinhalt. Sie werden als TEXT ausgegeben, nie als vom
 * Browser interpretiertes Markup — der Ueberschrift-Ausschnitt ist HTML und wird genau
 * deshalb sichtbar als Quelltext gezeigt, nicht gerendert.
 */
import type {
  BestehendesBallset,
  LaufendeAnlage,
  OriginalAnzeige,
  OriginalVarianteAnzeige,
  VorschauWarnung,
  WriteSchaetzung,
  ZielWerte,
} from '../typen';
import { eurOderStrich, PREIS_AB_MENGE_4, PREIS_SPALTEN, zahl, zeitpunkt } from '../formate';

const PANEL = 'border border-[#DAD9D9] rounded-lg bg-white p-4';

/** Attribute einer Variante als „Name: Wert", leer bleibt leer (nicht „undefined"). */
function attributeText(attribute: Array<{ attributName: string; wertName: string }>): string {
  const teile = attribute
    .map((a) => `${a.attributName}: ${a.wertName}`.trim())
    .filter((t) => t !== ':');
  return teile.length > 0 ? teile.join(' · ') : '—';
}

/** Eine Zeile „Bezeichnung → alter Wert / neuer Wert" fuer den Gegenueberstellungs-Block. */
function AltNeu({ titel, alt, neu }: { titel: string; alt: string; neu: string }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-[10rem_1fr_1fr] gap-1 md:gap-3 py-1.5 border-b border-[#DAD9D9] last:border-0">
      <div className="text-xs font-medium text-[#1A1A1A]">{titel}</div>
      <div className="text-xs text-gray-500 break-words">{alt || '—'}</div>
      <div className="text-xs text-[#1A1A1A] break-words">{neu || '—'}</div>
    </div>
  );
}

// ---------- Block 1: Original ----------

export function OriginalBlock({ original }: { original: OriginalAnzeige }) {
  return (
    <div className={PANEL}>
      <h2 className="text-sm font-bold text-[#1A1A1A]">
        Original · Artikel {original.itemId}
        {original.istSpielball && (
          <span className="ml-2 rounded bg-[#E9FF57] px-1.5 py-0.5 text-[10px] font-semibold text-[#1A1A1A]">
            Spielball
          </span>
        )}
      </h2>
      <p className="text-xs text-gray-600 mt-0.5">{original.texte.name1}</p>

      <dl className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-xs">
        <div className="flex gap-2">
          <dt className="text-gray-500 w-32 shrink-0">Variantennummer</dt>
          <dd className="text-[#1A1A1A]">{original.variantenNr}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="text-gray-500 w-32 shrink-0">Bilder</dt>
          <dd className="text-[#1A1A1A]">
            {zahl(original.bilder.anzahl)}
            {original.bilder.variantenVerknuepft ? ' (am Original variantenverknüpft — im Ballset am Artikel)' : ''}
            {original.bilder.dateinamen.length > 0 && (
              <span className="text-gray-500"> · {original.bilder.dateinamen.join(', ')}</span>
            )}
          </dd>
        </div>
        <div className="flex gap-2">
          <dt className="text-gray-500 w-32 shrink-0">Kategorien</dt>
          <dd className="text-[#1A1A1A]">
            {original.kategorien.length === 0
              ? '—'
              : original.kategorien.map((k) => `${k.pfad} (${k.id})`).join(' · ')}
          </dd>
        </div>
        <div className="flex gap-2">
          <dt className="text-gray-500 w-32 shrink-0">Versandprofile</dt>
          <dd className="text-[#1A1A1A]">
            {original.versandprofile.length === 0 ? '—' : original.versandprofile.join(', ')}
          </dd>
        </div>
        <div className="flex gap-2">
          <dt className="text-gray-500 w-32 shrink-0">Merkmale &amp; Tags</dt>
          <dd className="text-[#1A1A1A]">{zahl(original.merkmaleUndTags)}</dd>
        </div>
      </dl>

      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-[#1A1A1A] text-white">
            <tr>
              <th className="px-2 py-1.5 text-left font-medium">Variante</th>
              <th className="px-2 py-1.5 text-left font-medium">Attribute</th>
              {PREIS_SPALTEN.map((s) => (
                <th key={s.id} className="px-2 py-1.5 text-right font-medium">
                  {s.titel}
                </th>
              ))}
              <th className="px-2 py-1.5 text-right font-medium">{PREIS_AB_MENGE_4.titel}</th>
              <th className="px-2 py-1.5 text-right font-medium">EK</th>
              <th className="px-2 py-1.5 text-right font-medium">Position</th>
            </tr>
          </thead>
          <tbody>
            {original.varianten.map((v) => (
              <tr key={v.id} className="border-b border-[#DAD9D9] last:border-0">
                <td className="px-2 py-1.5 whitespace-nowrap">
                  {v.number}
                  <span className="text-gray-400"> #{v.id}</span>
                  {v.istHauptvariante && <span className="text-gray-500"> · Hauptvariante</span>}
                  <span
                    className={`ml-1.5 rounded px-1.5 py-0.5 text-[10px] ${
                      v.istAktiv ? 'bg-green-100 text-green-900' : 'bg-red-100 text-red-900'
                    }`}
                  >
                    {v.istAktiv ? 'aktiv' : 'inaktiv'}
                  </span>
                </td>
                <td className="px-2 py-1.5">{attributeText(v.attribute)}</td>
                {PREIS_SPALTEN.map((s) => (
                  <td key={s.id} className="px-2 py-1.5 text-right whitespace-nowrap">
                    {eurOderStrich(v.preise[s.id])}
                  </td>
                ))}
                <td className="px-2 py-1.5 text-right whitespace-nowrap">
                  {eurOderStrich(v.preise[PREIS_AB_MENGE_4.id])}
                </td>
                <td className="px-2 py-1.5 text-right whitespace-nowrap">
                  {eurOderStrich(v.purchasePrice)}
                </td>
                <td className="px-2 py-1.5 text-right">{zahl(v.position)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- Block 2: Neuer Artikel ----------

export function ZielBlock({ original, ziel }: { original: OriginalAnzeige; ziel: ZielWerte }) {
  const altePosition = original.varianten.find((v) => v.istHauptvariante)?.position ?? null;
  const alteKategorien = original.kategorien.map((k) => k.id);

  return (
    <div className={PANEL}>
      <h2 className="text-sm font-bold text-[#1A1A1A]">Neuer Artikel</h2>
      <div className="mt-1 grid grid-cols-1 md:grid-cols-[10rem_1fr_1fr] gap-1 md:gap-3 text-[11px] uppercase tracking-wide text-gray-400">
        <div />
        <div>Original</div>
        <div>Ballset</div>
      </div>

      <AltNeu titel="Name 1" alt={original.texte.name1} neu={ziel.name1} />
      <AltNeu
        titel="Meta-Beschreibung"
        alt={original.texte.metaDescription}
        neu={ziel.metaDescription}
      />
      <AltNeu
        titel="Position"
        alt={altePosition === null ? '—' : zahl(altePosition)}
        neu={zahl(ziel.position)}
      />

      <div className="py-1.5 border-b border-[#DAD9D9]">
        <div className="text-xs font-medium text-[#1A1A1A]">Kategorien</div>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {ziel.kategorien.map((k) => (
            <span
              key={k.id}
              className={`rounded px-1.5 py-0.5 text-[11px] ${
                k.neu
                  ? 'bg-[#E9FF57] text-[#1A1A1A] font-semibold'
                  : 'bg-gray-100 text-[#1A1A1A]'
              }`}
              title={alteKategorien.includes(k.id) ? 'schon am Original' : 'kommt hinzu'}
            >
              {k.pfad} ({k.id}){k.neu ? ' · neu' : ''}
            </span>
          ))}
        </div>
      </div>

      <div className="py-1.5">
        <div className="text-xs font-medium text-[#1A1A1A]">Überschrift im Artikeltext</div>
        {/* Bewusst als Quelltext, nicht gerendert: der Ausschnitt ist fremdes HTML aus Plenty. */}
        <pre className="mt-1 whitespace-pre-wrap break-words rounded bg-gray-50 px-2 py-1.5 text-[11px] text-gray-700">
          {ziel.ueberschriftHtmlAusschnitt || '—'}
        </pre>
      </div>
    </div>
  );
}

// ---------- Block 4: Warnungen ----------

export function WarnungenBlock({ warnungen }: { warnungen: VorschauWarnung[] }) {
  return (
    <div className={PANEL}>
      <h2 className="text-sm font-bold text-[#1A1A1A]">
        Warnungen {warnungen.length > 0 && <span className="text-gray-500">({warnungen.length})</span>}
      </h2>
      {warnungen.length === 0 ? (
        <p className="mt-1 text-xs text-gray-500 italic">
          keine Warnungen — alles Nötige ist am Original gepflegt
        </p>
      ) : (
        <div className="mt-2">
          {warnungen.map((w, i) => (
            <div
              key={`${w.code}-${w.bezug ?? ''}-${i}`}
              className="mb-2 last:mb-0 text-xs bg-yellow-50 border border-yellow-200 text-yellow-900 rounded px-2 py-1.5"
            >
              {w.text}
              {w.bezug && <span className="text-yellow-700"> · {w.bezug}</span>}
              <span className="text-yellow-600 font-mono"> [{w.code}]</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Block 4b: Doppelanlage-Schutz (EING-05) ----------

/**
 * Die Ansage, dass es zu diesem Original schon ein Ballset gibt — oder gerade eines
 * entsteht. Zwei Faelle, die bewusst verschieden ausgehen:
 *
 *   `laufendeAnlage`      sperrt HART. Es gibt keine Checkbox, keine Uebersteuerung: zwei
 *                         gleichzeitige Laeufe legen zwei Ballsets an, und das zweite
 *                         muesste jemand von Hand aus dem Shop raeumen. Warten kostet
 *                         Minuten, Aufraeumen kostet einen Nachmittag.
 *   `bestehendesBallset`  ist eine Warnung mit Ausweg. Es gibt gute Gruende fuer ein
 *                         zweites Ballset (der erste Lauf lieferte Murks, das Original hat
 *                         neue Varianten) — deshalb die Checkbox „Trotzdem anlegen".
 *
 * Gelb wie die Warnzeilen darueber, mit dunkler Schrift (`text-yellow-900`): weisse Schrift
 * auf Gelb ist im Repo verboten und waere hier ohnehin unlesbar.
 *
 * Die Checkbox ist NUR Bequemlichkeit. Verbindlich prueft die schreibende Route in Phase 4
 * noch einmal gegen das Protokoll — ein Frontend-Zustand ist keine Freigabe (Threat T-03-18).
 */
export function BestehendesBallsetBlock({
  bestehendesBallset,
  laufendeAnlage,
  trotzdemAnlegen,
  onTrotzdemAnlegen,
}: {
  bestehendesBallset: BestehendesBallset | null;
  laufendeAnlage: LaufendeAnlage | null;
  trotzdemAnlegen: boolean;
  onTrotzdemAnlegen: (wert: boolean) => void;
}) {
  if (!bestehendesBallset && !laufendeAnlage) return null;

  return (
    <div className="rounded-lg border border-yellow-300 bg-yellow-50 text-yellow-900 p-4">
      {laufendeAnlage ? (
        <>
          <p className="text-sm font-semibold">
            Zu diesem Original läuft gerade eine Anlage (Lauf #{laufendeAnlage.laufId})
          </p>
          <p className="mt-1 text-xs">
            Gestartet {zeitpunkt(laufendeAnlage.gestartetAm)}
            {laufendeAnlage.gestartetVon && ` von ${laufendeAnlage.gestartetVon}`}. Bitte den
            Lauf abwarten — zwei gleichzeitige Läufe legen zwei Ballsets an. Ein zweites
            Ballset lässt sich nicht übersteuern, es müsste von Hand aus dem Shop entfernt
            werden.
          </p>
        </>
      ) : bestehendesBallset && (
        <>
          <p className="text-sm font-semibold">
            {bestehendesBallset.status === 'fehlgeschlagen' ? (
              <>
                Ein Lauf vom {zeitpunkt(bestehendesBallset.angelegtAm, true)} wurde
                abgebrochen, nachdem er Artikel #{bestehendesBallset.ballsetItemId} angelegt
                hatte (Lauf #{bestehendesBallset.laufId})
              </>
            ) : (
              <>
                Zu diesem Original wurde am {zeitpunkt(bestehendesBallset.angelegtAm, true)} bereits
                Ballset #{bestehendesBallset.ballsetItemId} angelegt (Lauf #{bestehendesBallset.laufId})
                {bestehendesBallset.status === 'teilweise' && ' — der Lauf war unvollständig'}
              </>
            )}
          </p>
          {bestehendesBallset.status === 'fehlgeschlagen' && (
            <p className="mt-1 text-xs">
              Dieser Artikel ist ein <strong>unvollständiger Rest</strong> eines
              abgebrochenen Laufs: Er steht inaktiv in Plenty, ihm fehlen Set-Varianten,
              Preise oder Pakete. Ein neuer Lauf repariert ihn <strong>nicht</strong>, er
              legt einen zweiten Artikel an. Artikel #{bestehendesBallset.ballsetItemId} in
              Plenty prüfen und löschen — oder bewusst „Trotzdem anlegen" setzen.
            </p>
          )}
          {bestehendesBallset.status === 'teilweise' && (
            <p className="mt-1 text-xs">
              Artikel, Set-Varianten und Pakete stehen vollständig in Plenty; ein Teil der
              Kopie (Bilder, Eigenschaften, Tags, Cross-Selling) fehlt. Diese Inhalte lassen
              sich in Plenty nachziehen — ein zweiter Lauf legt ein <strong>zweites</strong>{' '}
              Ballset an, er repariert das erste nicht.
            </p>
          )}
          <p className="mt-1 text-xs">
            {bestehendesBallset.gestartetVon
              ? `Gestartet von ${bestehendesBallset.gestartetVon}.`
              : 'Der Starter des Laufs ist nicht protokolliert.'}
            {bestehendesBallset.ballsetHauptVariationId
              && ` Hauptvariante #${bestehendesBallset.ballsetHauptVariationId}.`}
            {' '}Gemeldet wird nur, was im Protokoll dieses Tools steht — von Hand angelegte
            Ballsets kennt es nicht.
          </p>
          <label className="mt-2.5 flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={trotzdemAnlegen}
              onChange={(e) => onTrotzdemAnlegen(e.target.checked)}
              className="h-4 w-4 rounded border-[#DAD9D9] accent-[#E9FF57]"
            />
            <span>Trotzdem anlegen</span>
          </label>
        </>
      )}
    </div>
  );
}

// ---------- Block 4c: Was vom Original kopiert wird (KOP-07) ----------

/**
 * Die Zahl der DISTINKTEN Marktplaetze ueber alle Original-Varianten — oder `null`, wenn
 * die Antwort die Marktplaetze gar nicht traegt (aeltere Instanz).
 *
 * Distinkt, weil jede Set-Variante die Marktplaetze IHRER Original-Variante uebernimmt: die
 * Summe der Listen waere eine Zahl, die kein Mensch wiedererkennt, die Vereinigung dagegen
 * beantwortet die Frage „auf wie vielen Marktplaetzen landet das Ballset".
 *
 * `null` und `0` sind verschieden und werden verschieden behandelt: `null` heisst „wissen
 * wir nicht" und laesst die Zeile weg, `0` heisst „keine" und laesst sie ebenfalls weg —
 * aber aus dem anderen Grund. Behauptet wird in keinem der beiden Faelle etwas.
 */
function marktplatzAnzahl(varianten: OriginalVarianteAnzeige[]): number | null {
  const ids = new Set<number>();
  let gelesen = false;
  for (const v of varianten) {
    const liste = v.rohStammdaten?.variationMarkets;
    if (!liste) continue;
    gelesen = true;
    for (const id of liste) ids.add(id);
  }
  return gelesen ? ids.size : null;
}

/**
 * Wie viele Barcodes am Original haengen — ueber alle Varianten zusammen.
 *
 * Der einzige Posten dieses Blocks, der den Lauf noch Schreibvorgaenge kostet, und zwar in
 * die andere Richtung: Die Plenty-Kopie bringt die Barcodes des Originals MIT, und genau
 * das darf nicht bleiben. Dieselbe EAN an zwei Artikeln ist harmlos, solange der neue
 * inaktiv ist — und ein echtes Datenproblem, sobald ihn jemand freischaltet. Der Lauf
 * entfernt sie deshalb wieder (je Barcode ein `DELETE`).
 */
function barcodeAnzahl(varianten: OriginalVarianteAnzeige[]): number {
  // 🔴 Gezaehlt wird die ADRESS-Liste `barcodeEintraege` — dieselbe, aus der der Schreibplan
  // seine Loeschschritte baut. `barcodes` ist die ANZEIGE-Liste: sie wirft Eintraege mit
  // leerem `code` weg, die trotzdem einen DELETE kosten. Diese Zeile macht eine Aussage
  // ueber das Loeschen, also muss sie die Liste des Plans zaehlen — sonst widersprechen
  // sich „Barcodes: n" und die Nacharbeits-Zahl im Dialog (Review WR-08).
  // Der Rueckfall gilt einer aelteren Antwort ohne das Feld: dann lieber die Anzeigeform als
  // eine gelogene 0.
  return varianten.reduce(
    (n, v) => n + (v.barcodeEintraege?.length ?? v.barcodes?.length ?? 0), 0,
  );
}

/** Eine Zeile des Kopie-Rasters — dasselbe `<dl>`-Muster wie im Original-Block. */
function KopieZeile({ titel, wert }: { titel: string; wert: string }) {
  return (
    <div className="flex gap-2">
      <dt className="text-gray-500 w-32 shrink-0">{titel}</dt>
      <dd className="text-[#1A1A1A]">{wert}</dd>
    </div>
  );
}

/**
 * Was der Lauf vom Original uebernimmt — vor dem Klick, mit Anzahlen.
 *
 * Der Block beantwortet genau eine Frage: „Was passiert da gleich?" Bis Phase 4 war die
 * Antwort „der nackte Artikel", seit Phase 5 „der Artikel mit seinem ganzen Beiwerk" — und
 * seit Phase 7 bringt dieses Beiwerk die Plenty-Kopie mit, statt Eintrag fuer Eintrag
 * geschrieben zu werden. Beim Vorbild 16459 kostet der ganze Lauf dadurch 8 statt 36
 * Schreibvorgaenge. Wer das nicht vorher liest, haelt die neue Zahl fuer einen Fehler.
 *
 * ZEILEN MIT 0 WERDEN NICHT GEZEIGT — dieselbe Regel wie in der Write-Aufschluesselung:
 * eine Liste voller Nullen liest niemand mehr. Traegt die Antwort ein Feld gar nicht
 * (aeltere Instanz), faellt die Zeile ebenfalls weg, statt eine 0 zu behaupten.
 */
export function KopieBlock({ original }: { original: OriginalAnzeige }) {
  const quellen = original.kopierQuellen;
  const merkmale = quellen?.merkmale ?? [];
  const eigenschaften = quellen?.eigenschaften ?? [];
  const tagIds = quellen?.tagIds ?? [];
  const crossSelling = quellen?.crossSelling ?? [];
  const eigenschaftsWerte = eigenschaften.reduce((n, e) => n + (e.werte?.length ?? 0), 0);
  const maerkte = marktplatzAnzahl(original.varianten ?? []);
  const barcodes = barcodeAnzahl(original.varianten ?? []);

  const zeilen: Array<{ titel: string; wert: string }> = [];

  if (original.bilder.anzahl > 0) {
    zeilen.push({
      titel: 'Bilder',
      wert: `${zahl(original.bilder.anzahl)}${
        original.bilder.variantenVerknuepft ? ' · am Original variantenverknüpft' : ''
      }`,
    });
  }
  if (merkmale.length > 0) {
    zeilen.push({ titel: 'Merkmale', wert: zahl(merkmale.length) });
  }
  if (eigenschaften.length > 0) {
    zeilen.push({
      titel: 'Eigenschaften',
      wert: `${zahl(eigenschaften.length)}${
        eigenschaftsWerte > 0 ? ` · ${zahl(eigenschaftsWerte)} Werte` : ''
      }`,
    });
  }
  if (tagIds.length > 0) {
    zeilen.push({ titel: 'Tags', wert: zahl(tagIds.length) });
  }
  if (crossSelling.length > 0) {
    zeilen.push({ titel: 'Cross-Selling', wert: zahl(crossSelling.length) });
  }
  if (original.versandprofile.length > 0) {
    zeilen.push({
      titel: 'Versandprofile',
      wert: `${zahl(original.versandprofile.length)} · ${original.versandprofile.join(', ')}`,
    });
  }
  if (maerkte !== null && maerkte > 0) {
    zeilen.push({ titel: 'Marktplätze', wert: zahl(maerkte) });
  }
  // Letzte Zeile, weil sie als einzige nicht bleibt: die Kopie bringt die Barcodes mit, der
  // Lauf entfernt sie wieder. Bei 0 faellt sie weg wie jede andere.
  if (barcodes > 0) {
    zeilen.push({
      titel: 'Barcodes',
      wert: `${zahl(barcodes)} — werden am neuen Artikel wieder entfernt`,
    });
  }

  return (
    <div className={PANEL}>
      <h2 className="text-sm font-bold text-[#1A1A1A]">Wird vom Original übernommen</h2>
      {zeilen.length === 0 ? (
        <p className="mt-1 text-xs text-gray-500 italic">Vom Original ist nichts zu kopieren.</p>
      ) : (
        <>
          <p className="mt-1 text-xs text-gray-600">
            Diese Inhalte bringt die <strong className="text-[#1A1A1A]">Plenty-Kopie</strong>{' '}
            mit — sie kosten keinen eigenen Schreibvorgang mehr.
          </p>
          <dl className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-xs">
            {zeilen.map((z) => (
              <KopieZeile key={z.titel} titel={z.titel} wert={z.wert} />
            ))}
          </dl>
        </>
      )}
    </div>
  );
}

// ---------- Block 5: Write-Schaetzung ----------

export function WriteSchaetzungBlock({ writesGeschaetzt }: { writesGeschaetzt: WriteSchaetzung }) {
  return (
    <div className={PANEL}>
      <h2 className="text-sm font-bold text-[#1A1A1A]">Plenty-Schreibvorgänge</h2>
      <div className="mt-1 flex items-baseline gap-2">
        <span className="text-3xl font-bold text-[#1A1A1A]">{zahl(writesGeschaetzt.gesamt)}</span>
        <span className="text-xs text-gray-500">geschätzt für den ganzen Lauf</span>
      </div>
      <ul className="mt-2 text-xs">
        {writesGeschaetzt.aufschluesselung.map((z) => (
          <li
            key={z.schritt}
            className="flex justify-between border-b border-[#DAD9D9] last:border-0 py-1"
          >
            <span className="text-gray-600">{z.schritt}</span>
            <span className="text-[#1A1A1A]">{zahl(z.anzahl)}</span>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-[11px] text-gray-500">
        Schätzung — Plenty erlaubt 75 Writes pro Stunde. Liegt die Zahl darüber, dauert der
        Lauf entsprechend länger; der Core drosselt selbst.
      </p>
    </div>
  );
}
